import { useRef, useEffect } from 'react';

import { type PaymentMethod } from '@order/features-ordering';

/**
 * The {selectedPaymentMethod} is controlled by Stripe, so we don't have access to its callback or a "onPaymentMethodChanged", we can only sync via {useEffect}.
 * If a payment method is changed to a different non empty one, it must close the changing payment modal.
 * If it was removed, the list must be refetched as it would contain the stale one.
 */
export const useUpdatePaymentListOnChange = (props: Props) => {
  const {
    isShowingModal,
    selectedPaymentMethod,
    paymentMethods,
    onPaymentMethodChanged,
    onPaymentMethodRemoved,
  } = props;

  const paymentMethodsRef = useRef(paymentMethods);
  const isShowingModalRef = useRef(isShowingModal);
  const onPaymentMethodChangedRef = useRef(onPaymentMethodChanged);
  const onPaymentMethodRemovedRef = useRef(onPaymentMethodRemoved);
  const selectedPaymentMethodRef = useRef(selectedPaymentMethod);
  const previousPaymentMethod = selectedPaymentMethodRef.current;

  paymentMethodsRef.current = paymentMethods;
  selectedPaymentMethodRef.current = selectedPaymentMethod;
  isShowingModalRef.current = isShowingModal;
  onPaymentMethodChangedRef.current = onPaymentMethodChanged;
  onPaymentMethodRemovedRef.current = onPaymentMethodRemoved;

  const hasChangedPaymentMethod = selectedPaymentMethod !== previousPaymentMethod;

  useEffect(() => {
    if (!hasChangedPaymentMethod || !isShowingModalRef.current) return;

    const paymentMethod = selectedPaymentMethodRef.current;
    const paymentMethodList = paymentMethodsRef.current;

    // Do not close the changing payment modal if a payment was removed.
    if (paymentMethod) onPaymentMethodChangedRef.current();

    const wasPaymentMethodRemoved = !paymentMethod;
    const wasListAlreadyEmpty = paymentMethodList.length === 0;

    if (!wasPaymentMethodRemoved || wasListAlreadyEmpty) return;

    onPaymentMethodRemovedRef.current();
  }, [hasChangedPaymentMethod]);
};

// ─── Type ────────────────────────────────────────────────────────────────────

type Props = {
  isShowingModal: boolean;
  selectedPaymentMethod: string | undefined;
  paymentMethods: readonly PaymentMethod[];
  onPaymentMethodChanged: () => void;
  onPaymentMethodRemoved: () => void;
};
